<script setup lang="ts">
import "@/assets/css/vendors/tom-select.css";
import _ from "lodash";
import {
  setValue,
  init,
  updateValue,
  destroyTomSelectInstance,
  type TomSelectHostElement,
} from "./tom-select";
import {
  type TomSettings,
  type RecursivePartial,
} from "tom-select/src/types/index";
import TomSelectPlugin from "tom-select";
import {
  computed,
  type SelectHTMLAttributes,
  onMounted,
  inject,
  ref,
} from "vue";

export interface TomSelectElement extends HTMLSelectElement {
  TomSelect: TomSelectPlugin;
}

export interface TomSelectProps extends /* @vue-ignore */ SelectHTMLAttributes {
  modelValue: string | string[];
  options?: RecursivePartial<TomSettings>;
  refKey?: string;
}

export interface TomSelectEmit {
  (e: "update:modelValue", value: string | string[]): void;
  (e: "optionAdd", value: string | number): void;
}

export type ProvideTomSelect = (el: TomSelectElement) => void;

const props = withDefaults(defineProps<TomSelectProps>(), {});

const emit = defineEmits<TomSelectEmit>();

const tomSelectRef = ref<TomSelectElement>();

// Compute all default options
const computedOptions = computed(() => {
  let options: TomSelectProps["options"] = {
    ...props.options,
    plugins: {
      dropdown_input: {},
      ...props.options?.plugins,
    },
  };

  if (Array.isArray(props.modelValue)) {
    options = {
      persist: false,
      create: true,
      onDelete: function (values: string[]) {
        return true;
      },
      ...options,
      plugins: {
        remove_button: {
          title: "Eliminar este elemento",
        },
        ...options.plugins,
      },
    };
  }

  return options;
});

const vSelectDirective = {
  mounted(el: TomSelectElement) {
    const host = el as TomSelectHostElement;

    // Unique attribute
    host.setAttribute("data-id", "_" + Math.random().toString(36).substr(2, 9));

    // Clone the select element to prevent tom select remove the original element
    const clonedEl = host.cloneNode(true) as TomSelectElement;

    // Save initial classnames
    const classNames = host.getAttribute("class");
    classNames && clonedEl.setAttribute("data-initial-class", classNames);
    clonedEl.setAttribute("data-id", host.getAttribute("data-id") ?? "");

    host.__tomSelectClone = clonedEl;

    // Hide original element
    host.parentNode?.appendChild(clonedEl);
    host.setAttribute("hidden", "true");

    // Initialize tom select
    setValue(clonedEl, props);
    init(host, clonedEl, props, computedOptions.value, emit);
  },
  updated(el: TomSelectElement) {
    const host = el as TomSelectHostElement;
    const clonedEl = host.__tomSelectClone;
    if (!clonedEl) {
      return;
    }

    updateValue(host, clonedEl, props.modelValue, props, computedOptions.value, emit);
  },
  unmounted(el: TomSelectElement) {
    destroyTomSelectInstance(el as TomSelectHostElement);
  },
};

const bindInstance = (el: TomSelectElement) => {
  if (props.refKey) {
    const bind = inject<ProvideTomSelect>(`bind[${props.refKey}]`);
    if (bind) {
      bind(el);
    }
  }
};

onMounted(() => {
  if (tomSelectRef.value) {
    bindInstance(tomSelectRef.value);
  }
});
</script>

<template>
  <select
    ref="tomSelectRef"
    :value="props.modelValue"
    @change="
      (event) => {
        emit('update:modelValue', (event.target as HTMLSelectElement).value);
      }
    "
    v-select-directive
    class="tom-select"
  >
    <slot></slot>
  </select>
</template>
